import { Building2, CalendarDays, Info, MapPin } from "lucide-react";
import { inr, num } from "../lib/format";

/** Section heading used across every drawer and detail panel. */
export const EvidenceSection = ({ label, note, children, testId }) => (
  <section className="space-y-2.5" data-testid={testId}>
    <div className="flex items-baseline justify-between gap-3">
      <h3 className="text-[13px] font-semibold text-stone-800">
        {label}
      </h3>
      {note && <span className="text-[12px] text-stone-600 num">{note}</span>}
    </div>
    {children}
  </section>
);

/** Lead sentence of a drawer — the answer in one line, on a branded surface. */
export const SummaryBanner = ({ children, testId = "evidence-summary" }) => (
  <div className="rounded-xl border border-[#F3C6D0] bg-[#FDF2F4] px-4 py-3.5 flex items-start gap-3"
       data-testid={testId}>
    <Info className="w-4 h-4 text-[#A01236] shrink-0 mt-0.5" />
    <p className="text-[14px] leading-relaxed text-stone-800">{children}</p>
  </div>
);

/** A caveat that must read as method, not fine print. */
export const CaveatNote = ({ title = "How to read this", children, testId = "evidence-caveat" }) => (
  <div className="border-l-[3px] border-[#A01236] bg-stone-100/80 rounded-r-lg px-4 py-3 space-y-1.5"
       data-testid={testId}>
    <div className="text-[12px] font-semibold text-[#A01236]">
      {title}
    </div>
    <p className="text-[13px] text-stone-700 leading-relaxed">{children}</p>
  </div>
);

/** Key-value pairs for a record's metadata. */
const Meta = ({ Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 text-[12px] text-stone-600">
    <Icon className="w-3 h-3 text-stone-500 shrink-0" />
    <span className="truncate">{children}</span>
  </span>
);

/**
 * One traced grant, rendered as evidence rather than a text dump: what the grant is, whose it is,
 * what share of it lands in this geography, and how much of that share is apportionment.
 */
export const GrantRecord = ({ grant, stateName, testId }) => {
  const share = grant.committed_total
    ? Math.max(1, Math.round((100 * grant.committed_in_state) / grant.committed_total))
    : 0;
  const panIndia = grant.states_split_across >= 6;
  const single = grant.states_split_across === 1;
  return (
    <article
      className="rounded-lg border border-stone-200 bg-white p-4 space-y-3 transition-colors hover:border-stone-300"
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-3">
        <h4 className="text-[14px] font-semibold text-stone-900 leading-snug">
          {grant.grant_name}
        </h4>
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[12px] font-medium border shrink-0 ${
          single
            ? "bg-emerald-50 text-emerald-800 border-emerald-200"
            : panIndia
              ? "bg-amber-50 text-amber-800 border-amber-200"
              : "bg-stone-100 text-stone-700 border-stone-200"
        }`}>
          {single ? "state-specific" : panIndia ? "pan-India" : `${grant.states_split_across} states`}
        </span>
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-1.5">
        <Meta Icon={CalendarDays}>{grant.fy}</Meta>
        <Meta Icon={Building2}>{grant.partner}</Meta>
        {grant.grant_id && (
          <span className="num text-[12px] text-stone-500">{grant.grant_id}</span>
        )}
      </div>

      <div className="rounded-md border border-stone-100 bg-stone-50 p-2.5 space-y-2">
        <div className="flex items-baseline justify-between gap-3">
          <span className="num text-[14px] font-semibold text-stone-900">
            {inr(grant.committed_in_state)}
          </span>
          <span className="text-[12px] text-stone-600">
            of {inr(grant.committed_total)} committed
          </span>
        </div>
        <div className="w-full bg-stone-200 h-1.5 rounded-full overflow-hidden">
          <div className="bg-[#A01236] h-full rounded-full transition-all duration-500"
               style={{ width: `${Math.min(100, share)}%` }} />
        </div>
        <div className="text-[12px] text-stone-600 leading-snug">
          {single
            ? `Entirely for ${stateName || "this state"}.`
            : `Even split across ${num(grant.states_split_across)} states${panIndia ? " — apportioned, not a commitment to " + (stateName || "this state") : ""}.`}
        </div>
      </div>
    </article>
  );
};

/** Compact stat used in drawers and detail panels. The accent carries the meaning. */
const TILE_TONES = {
  brand: ["border-l-[#A01236]", "text-[#A01236]"],
  emerald: ["border-l-emerald-500", "text-emerald-700"],
  amber: ["border-l-amber-500", "text-amber-700"],
  rose: ["border-l-rose-500", "text-rose-700"],
  sky: ["border-l-sky-500", "text-sky-700"],
  neutral: ["border-l-stone-300", "text-stone-600"],
};

export const StatTile = ({ label, value, sub, title, tone = "neutral", testId }) => {
  const [bar, ink] = TILE_TONES[tone] || TILE_TONES.neutral;
  return (
    <div
      className={`rounded-lg border border-stone-200 border-l-[3px] ${bar} bg-white px-4 py-3 transition-colors hover:border-stone-300`}
      title={title}
      data-testid={testId}
    >
      <div className={`text-[12px] font-semibold ${ink}`}>{label}</div>
      <div className="num text-[17px] font-semibold text-stone-900 mt-1 leading-tight">{value}</div>
      {sub && <div className="text-[12px] text-stone-600 mt-1 leading-snug">{sub}</div>}
    </div>
  );
};

/** Tone for a 0-100 utilisation-style figure. */
export const rateTone = (v) =>
  v === null || v === undefined ? "neutral" : v >= 85 ? "emerald" : v >= 60 ? "amber" : "rose";

export const PlaceChip = ({ children }) => (
  <span className="inline-flex items-center gap-1 text-[12px] text-stone-700 bg-stone-100 border border-stone-200 rounded-full px-2 py-0.5">
    <MapPin className="w-3 h-3 text-stone-500" />
    {children}
  </span>
);
